import { teams, workspaces, SaveWorkspaceParam, profiles } from '@chilipiper/service'
import {
  UserAccess,
  Workspace,
  WorkspaceType,
  MeetingsProductType,
  RecentWorkspace,
} from '@chilipiper/api-type-def'
import { SaveWorkspaceEventPayload } from '@chilipiper/api-type-def/src/events'
import { getSession } from '@chilipiper/service/src/session'
import { eventsService } from '@chilipiper/service/src/events'

import { rolloutFlags } from '@chilipiper/rollout'

import { getTemplateById } from './templates'
import { Session } from './session'

// This should be the real WorkspaceType and
// WorkspaceType should've been ProductType 🧐🤷‍♀️
export enum WorkspaceFeatureType {
  Distro = 'Distribution',
  Free = 'Free',
  Hot = 'Hot',
  Mild = 'Mild',
}

export const shouldShowWorkspace = (workspace: Workspace, workspaceType: WorkspaceType) => {
  const products = getSession().products
  const shouldReturnDistribution =
    rolloutFlags.enableDistributionWorkspace?.isEnabled() ||
    !workspace.isDistribution ||
    products.includes('Distribution')
  return (
    workspace.workspaceType === workspaceType &&
    Workspaces.showWorkspaceFeatureType(workspace) &&
    shouldReturnDistribution
  )
}

export class Workspaces {
  static assigneUser(params: { activate: boolean; userId: string; workspaceId: string }) {
    return workspaces.assignUser(params)
  }

  static assigneAdmin(params: { status: boolean; userId: string }) {
    return teams.assignAdmin(params)
  }

  static loadBookingLinks(workspaceId: string) {
    return workspaces.bookingLinks(workspaceId)
  }

  static loadMembers(workspaceId: string) {
    return workspaces.getUsers(workspaceId)
  }

  static findUserQueues(userId: string, workspaceId: string) {
    return workspaces.userQueues(userId, workspaceId)
  }

  static userEnabled(workspace: Workspace, user: UserAccess) {
    if (workspace.workspaceType === 'CRM' || workspace.workspaceType === 'Recruitment') {
      return (
        user.licences.meetings.status === 'Active' ||
        user.licences.mildMeetings.status === 'Active' ||
        user.licences?.concierge?.status === 'Active'
      )
    }

    if (workspace.workspaceType === 'Events') {
      return user.licences.events.status === 'Active'
    }

    throw new Error('Not implemented')
  }

  static typeToLabel(workspaceType: WorkspaceType) {
    if (workspaceType === 'CRM') {
      return 'Meetings'
    }

    return workspaceType
  }

  static workspaceFeatureType(workspace: Workspace) {
    if (workspace.isDistribution) {
      return WorkspaceFeatureType.Distro
    }

    if (!workspace.productTypes) {
      return WorkspaceFeatureType.Hot
    }

    switch (workspace.productTypes.meetingsType) {
      case 'Mild':
        return WorkspaceFeatureType.Mild
      case 'Free':
        return WorkspaceFeatureType.Free
      default:
        return WorkspaceFeatureType.Hot
    }
  }

  static isHot(workspace?: Workspace) {
    if (!workspace) {
      return false
    }
    return Workspaces.workspaceFeatureType(workspace) === WorkspaceFeatureType.Hot
  }

  static isMild(workspace?: Workspace) {
    if (!workspace) {
      return false
    }
    return Workspaces.workspaceFeatureType(workspace) === WorkspaceFeatureType.Mild
  }

  static isFree(workspace?: Workspace) {
    if (!workspace) {
      return false
    }
    return Workspaces.workspaceFeatureType(workspace) === WorkspaceFeatureType.Free
  }

  static isEventsType(workspace?: Workspace) {
    if (!workspace) {
      return false
    }
    return workspace.workspaceType === 'Events'
  }

  static workspaceTypeIsEnabled(workspace: Workspace) {
    const products = getSession().products
    const workspaceFeatureType = Workspaces.workspaceFeatureType(workspace)
    if (workspaceFeatureType === WorkspaceFeatureType.Mild) {
      return products.includes('MildMeetings')
    }
    if (workspaceFeatureType === WorkspaceFeatureType.Free) {
      return products.includes('MildMeetings')
    } else {
      return products.includes('Meetings')
    }
  }

  static getById(workspaceId: string) {
    return workspaces.byId(workspaceId)
  }

  static getInfo(workspaceId: string) {
    return workspaces.getInfo(workspaceId)
  }

  static async getInviteLink(workspaceId: string) {
    const { link } = await workspaces.getInviteLink(workspaceId)
    return link
  }

  static async create(params: SaveWorkspaceParam, logo?: File | null) {
    let url = ''
    try {
      if (logo) {
        url = await profiles.uploadImage(logo)
      }
      return workspaces.save({
        ...params,
        logo: url || params.logo,
      })
    } catch (err) {
      return Promise.reject(err)
    }
  }

  static async saveEventsWorkspace(
    params: SaveWorkspaceParam,
    eventParams: Omit<SaveWorkspaceEventPayload, 'workspaceId'>,
    logo?: File | null
  ) {
    try {
      const workspace = await Workspaces.create(params, logo)
      const event = await eventsService.create({
        workspaceId: workspace.id,
        ...eventParams,
      })

      return { workspace, event }
    } catch (err) {
      return Promise.reject(err)
    }
  }

  static getMeetingsTypeHomeScreen(
    meetingsType: MeetingsProductType | undefined,
    isDistribution = false
  ) {
    if (isDistribution) {
      return 'crm-router'
    }
    switch (meetingsType) {
      case 'Hot':
        return 'queues/list'
      case 'Mild':
        return 'dashboard'
      case 'Free':
        return 'dashboard'
      case 'Distribution':
        return 'crm-router'
      default:
        return 'queues/list'
    }
  }

  static getWorkspaceHomeLink(workspace: Workspace | RecentWorkspace) {
    switch (workspace.type ?? workspace.workspaceType) {
      case 'CRM':
        return `/admin-center/meetings/${workspace.id}/${Workspaces.getMeetingsTypeHomeScreen(
          workspace.productTypes?.meetingsType,
          workspace.isDistribution
        )}`
      case 'Events':
        return `/admin-center/meetings/${workspace.id}/dashboard`
      case 'Recruitment':
        return `/recruitment/${workspace.id}/queues/list`
      default:
        return ''
    }
  }

  static showWorkspaceFeatureType(workspace: Workspace) {
    const products = getSession().products
    const workspaceFeatureType = Workspaces.workspaceFeatureType(workspace)

    if (workspaceFeatureType === WorkspaceFeatureType.Free) {
      return products.includes('MildMeetings')
    }

    return true
  }

  static getAll() {
    return workspaces.manage()
  }

  static async findWorkspaceForPersonalPage(templateId: string) {
    const template = await getTemplateById(templateId)

    const workspaceId = template.workspaceId || Session.getAssignedWorkspaceId()

    if (!workspaceId) {
      throw new Error('There is no workspace assigned for this user')
    }

    return Workspaces.getById(workspaceId)
  }
}
